import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Stack,
  IconButton,
  Grid,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import { motion } from "framer-motion";
import configServ from "../../services/config";
import { toast } from "react-hot-toast";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import CancelIcon from "@mui/icons-material/Cancel";
import SubMenuCard from "./SubMenuCard";
import LoaderBackdrop from "../../Components/Loader/LoaderBackdrop";
import { useDispatch } from "react-redux";
import { rSetFetchMenuList } from "../../redux/reducers/GlobalVariables";

const MenuCard = ({
  isEdit,
  setIsEdit,
  editData,
  isCreate,
  setIsCreate,
  fetchMenuList,
  permissionsList,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    icon: "",
    routes: "",
    permission: null,
    is_active: false,
    is_admin: false,
    is_customer: false,
    order_id: "",
    is_primary: false, // New field for primary menu
  });

  const [subMenus, setSubMenus] = useState([
    {
      name: "",
      routes: "",
      icon: "",
      permission: null,
      is_active: false,
      is_admin: false,
      is_customer: false,
      sub_order_id: "",
    },
  ]);

  const dispatch = useDispatch();

  const [isAddSubmenus, setIsAddSubmenus] = useState(false); // Checkbox state
  const [openBackdrop, setOpenBackdrop] = useState(false);
  // New state for selected permission
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [selectedPermissionSubMenus, setSelectedPermissionSubMenus] = useState([
    null,
  ]);

  useEffect(() => {
    setOpenBackdrop(true);
    if (isEdit && editData) {
      setFormData({
        id: editData.id || "",
        name: editData.name || "",
        icon: editData.icon || "",
        routes: editData.routes || "",
        permission: editData.permission || null,
        is_active: editData.is_active || false,
        is_admin: editData.is_admin || false,
        is_customer: editData.is_customer || false,
        order_id: editData.order_id || "",
        is_primary: editData.is_primary || false, // Set primary menu state

      });

      // Populate submenus
      if (editData.submenus && editData.submenus.length > 0) {
        setSubMenus(
          editData.submenus.map((submenu) => {
            // Find the matched permission object
            const matchedPermission = permissionsList
              .flatMap((item) => item.permissions)
              .find((permission) => permission.id === submenu.permission);

            return {
              id: submenu.id || "",
              name: submenu.name || "",
              icon: submenu.icon || "",
              routes: submenu.routes || "",
              permission: submenu.permission || null, // Keep only the permission ID
              is_active: submenu.is_active || false,
              is_admin: submenu.is_admin || false,
              is_customer: submenu.is_customer || false,
              sub_order_id: submenu.sub_order_id || "",
            };
          })
        );
      } else {
        setSubMenus([
          { name: "", routes: "", is_active: false, is_admin: false, sub_order_id: "" },
        ]);
      }

      // Pre-select main permission
      if (editData?.permission) {
        const matchedPermission = permissionsList
          .flatMap((item) => item.permissions)
          .find((permission) => permission.id === editData.permission);

        setSelectedPermission(matchedPermission || null);
      } else {
        setSelectedPermission(null);
      }

      // Automatically enable submenus if they exist
      setIsAddSubmenus(editData.submenus.length > 0);
    }
    setOpenBackdrop(false);
  }, [isEdit, editData, permissionsList]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle checkbox changes
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  // Reset the form
  const handleReset = () => {
    setFormData({
      name: "",
      routes: "",
      icon: "",
      permission: null,
      is_active: false,
      order_id: "",
      is_admin: false,
      is_customer: false,
      is_primary: false, // Reset primary menu state
    });

    setSubMenus([
      {
        name: "",
        routes: "",
        icon: "",
        permission: null,
        is_active: false,
        is_admin: false,
        is_customer: false,
        sub_order_id: "",
      },
    ]);

    setSelectedPermission(null); // Clear the main permission dropdown
    setSelectedPermissionSubMenus([]); // Reset selected permissions for submenus
    setIsAddSubmenus(false); // Uncheck Add Submenus
  };

  // Handle form submission validation
  const validateForm = () => {
    let isValid = true;

    if (!formData.name.trim()) {
      toast.error("Name field is required!");
      isValid = false;
    }
    // if (!formData.routes.trim()) {
    //   toast.error("Routes field is required!");
    //   isValid = false;
    // }
    // Validate submenus fields
    subMenus.forEach((submenu, index) => {
      if (
        (submenu.name.trim() && !submenu.routes.trim()) ||
        (!submenu.name.trim() && submenu.routes.trim())
      ) {
        toast.error(
          `Both Sub-Name and Sub-Routes fields are required for submenu #${index + 1
          }!`
        );
        isValid = false;
      }
    });

    // Add other validations here if needed
    return isValid;
  };

  // Submit the form
  const createMenu = async () => {
    if (!validateForm()) return; // Stop submission if validation fails

    // Filter out submenus that don't have both name and routes filled
    const validSubMenus = subMenus.filter(
      (submenu) => submenu.name.trim() && submenu.routes.trim()
    );
    const dataToSend = {
      ...formData, // Menu-level fields
      submenus: validSubMenus, // Submenu-level fields
    };
    try {
      setOpenBackdrop(true);
      const res = await configServ.menuCreate(dataToSend);
      if (res.status === 200) {
        toast.success("Menu created successfully!");
        handleReset(); // Reset the form after successful creation
        fetchMenuList();
        dispatch(rSetFetchMenuList(true));
      } else {
        toast.error("Menu creation failed!");
      }
    } catch (e) {
      console.log(e);
    } finally {
      setOpenBackdrop(false);
    }
  };

  // Update the menu
  const updateMenu = async () => {
    if (!validateForm()) return; // Stop submission if validation fails
    // Filter out submenus that don't have both name and routes filled
    const validSubMenus = subMenus.filter(
      (submenu) => submenu.name.trim() && submenu.routes.trim()
    );
    const dataToSend = {
      ...formData, // Menu-level fields
      submenus: validSubMenus, // Submenu-level fields
    };
    try {
      setOpenBackdrop(true);

      const res = await configServ.menuUpdate(dataToSend);
      if (res.status === 200) {
        toast.success("Menu updated successfully!");
        setIsEdit(false);
        handleReset(); // Reset the form after successful update
        fetchMenuList();
        dispatch(rSetFetchMenuList(true));
      } else {
        toast.error("Menu update failed!");
      }
    } catch (e) {
      toast.error("An error occurred!");
      console.log(e);
    } finally {
      setOpenBackdrop(false);
    }
  };

  const handleCut = () => {
    handleReset();
    setIsCreate(!isCreate);
    setIsEdit(false);
    setIsAddSubmenus(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: -20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -20 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      style={{
        width: "100%",
        backgroundColor: "#e3f3fc",
        padding: "20px",
        borderRadius: "10px",
        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
      }}
    >
      <LoaderBackdrop open={openBackdrop} />

      {/* Input Fields */}
      <Grid container spacing={2} sx={{ width: "100%", marginBottom: 2 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            name="name"
            label="Name"
            variant="outlined"
            value={formData.name}
            onChange={handleChange}
            fullWidth
            size="small"
            sx={{
              backgroundColor: "#fff",
              borderRadius: "4px",
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            name="routes"
            label="Routes Name"
            variant="outlined"
            value={formData.routes}
            onChange={handleChange}
            fullWidth
            size="small"
            sx={{
              backgroundColor: "#fff",
              borderRadius: "4px",
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            name="icon"
            label="icon"
            variant="outlined"
            value={formData.icon}
            onChange={handleChange}
            fullWidth
            size="small"
            sx={{
              backgroundColor: "#fff",
              borderRadius: "4px",
            }}
          />
        </Grid>
        {/* Permission Dropdown */}
        <Grid item xs={12} sm={4}>
          <Autocomplete
            value={selectedPermission} // Controlled value
            options={permissionsList.flatMap((item) =>
              item.permissions.map((permission) => ({
                category: item.PageAccessControl,
                name: permission.name,
                id: permission.id,
              }))
            )}
            groupBy={(option) => option.category} // Grouping by category
            getOptionLabel={(option) => option.name}
            onChange={(event, newValue) => {
              setSelectedPermission(newValue); // Update selected permission
              setFormData((prev) => ({
                ...prev,
                permission: newValue ? newValue.id : null, // Reset permission if no value is selected
              }));
            }}
            renderOption={(props, option) => {
              const isSelected = selectedPermission?.id === option.id; // Check if this option is selected
              return (
                <li
                  {...props}
                  style={{
                    backgroundColor: isSelected ? "#e0f7fa" : "transparent", // Highlight background for selected
                    fontWeight: isSelected ? "bold" : "normal", // Bold text for selected
                  }}
                >
                  <div style={{ marginLeft: "10px" }}>{option.name}</div>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Permission"
                variant="outlined"
                size="small"
                sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
              />
            )}
            ListboxProps={{
              style: { maxHeight: 300, overflow: "auto" }, // Scrollable container
            }}
            onOpen={() => {
              const allOptions = permissionsList.flatMap(
                (item) => item.permissions
              );
              const index = allOptions.findIndex(
                (permission) => permission.id === selectedPermission?.id
              );

              setTimeout(() => {
                const listbox = document.querySelector('[role="listbox"]'); // Access the dropdown element
                if (listbox && index !== -1) {
                  const optionElement = listbox.children[index];
                  if (optionElement) {
                    optionElement.scrollIntoView({ block: "nearest" });
                  }
                }
              }, 0); // Delay ensures the dropdown is fully rendered
            }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Checkbox
                name="is_active"
                checked={formData.is_active}
                onChange={handleCheckboxChange}
              />
            }
            label="Active"
            sx={{
              color: "#555",
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Checkbox
                name="is_admin"
                checked={formData.is_admin}
                onChange={handleCheckboxChange}
              />
            }
            label="Admin"
            sx={{
              color: "#555",
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Checkbox
                name="is_customer"
                checked={formData.is_customer}
                onChange={handleCheckboxChange}
              />
            }
            label="Customer"
            sx={{
              color: "#555",
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Checkbox
                name="is_primary"
                checked={formData.is_primary}
                onChange={handleCheckboxChange}
              />
            }
            label="Primary"
            sx={{
              color: "#555",
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            name="order_id"
            label="Order"
            variant="outlined"
            type="number"
            value={formData.order_id}
            onChange={handleChange}
            fullWidth
            size="small"
            sx={{
              backgroundColor: "#fff",
              borderRadius: "4px",
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Checkbox
                name="add_submenus"
                checked={isAddSubmenus}
                onChange={(e) => setIsAddSubmenus(e.target.checked)}
              />
            }
            label="Add Submenus"
            sx={{
              color: "#555",
            }}
          />
        </Grid>

      </Grid>

      {isAddSubmenus && (
        <SubMenuCard
          subMenus={subMenus}
          setSubMenus={setSubMenus}
          permissionsList={permissionsList}
          selectedPermissionSubMenus={selectedPermissionSubMenus}
          setSelectedPermissionSubMenus={setSelectedPermissionSubMenus}
        />
      )}

      {/* Buttons */}
      <Stack
        direction="row"
        spacing={2}
        sx={{
          width: "100%",
          justifyContent: "flex-end",
          marginTop: 2,
        }}
      >
        {isEdit ? (
          <IconButton
            onClick={handleCut}
            sx={{
              borderRadius: 2,
              px: 2,
              py: 1,
              backgroundColor: "red",
              color: "white",
              "&:hover": {
                backgroundColor: "darkred",
              },
              boxShadow: 2,
              ml: 1,
              transition: "all 0.3s ease",
            }}
            title="cut"
          >
            <CancelIcon sx={{ fontSize: 18 }} />
          </IconButton>
        ) : (
          <IconButton
            onClick={handleReset}
            sx={{
              borderRadius: 2,
              px: 2,
              py: 1,
              backgroundColor: "secondary.main",
              color: "white",
              "&:hover": {
                backgroundColor: "secondary.dark",
              },
              ml: 1,
            }}
            title="Reset"
          >
            <RotateLeftIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        <IconButton
          size="small"
          onClick={isEdit ? updateMenu : createMenu}
          sx={{
            borderRadius: 2,
            px: 2,
            py: 1,
            backgroundColor: isEdit ? "success.main" : "primary.main",
            color: "white",
            "&:hover": {
              backgroundColor: isEdit ? "success.dark" : "primary.dark",
            },
          }}
          title={isEdit ? "Update" : "Save"}
        >
          <SaveIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Stack>
    </motion.div>
  );
};

export default MenuCard;
